import {
  cloneElement,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/cn'

/**
 * Menu popover. `children` is a render prop receiving `close` so items can
 * dismiss the menu before running their action — a menu that stays open while
 * a drawer slides in behind it is the bug this shape prevents.
 */
export function Dropdown({
  trigger,
  align = 'left',
  width = 'w-44',
  children,
}: {
  trigger: ReactElement<{ onClick?: (e: React.MouseEvent) => void }>
  align?: 'left' | 'right'
  width?: string
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={root} className="relative inline-flex">
      {cloneElement(trigger, {
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation()
          setOpen((v) => !v)
        },
      })}
      {open && (
        <div
          role="menu"
          className={cn(
            'absolute top-full z-40 mt-1 overflow-hidden rounded-[6px] border border-rule',
            'bg-surface py-1 shadow-modal',
            align === 'right' ? 'right-0' : 'left-0',
            width,
          )}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}

export function DropdownItem({
  onClick,
  destructive = false,
  disabled = false,
  children,
}: {
  onClick: () => void
  destructive?: boolean
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px]',
        'disabled:cursor-not-allowed disabled:opacity-45',
        destructive ? 'text-brand-red-700 hover:bg-brand-red-50' : 'text-ink hover:bg-surface-sunk',
      )}
    >
      {children}
    </button>
  )
}

export function DropdownDivider() {
  return <div className="my-1 border-t border-rule" role="separator" />
}
